import React from 'react';
import TabMessagerie from '../../../composants/TabMessagerie/TabMessagerie';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import { CommandeMS, StatusId, TabKind } from '../types';
import { MessageChannel } from '../hooks/useCommandeMsUnreadCounts';
import { useChronoExpired } from '../hooks/useChronoExpired';
import { notificationMarche } from '../domain/commandeMsCandidature';
import { ctaVersEvaluation, ctaVersOffre } from '../domain/commandeMsNextTab';

interface CommandeMsMessagerieProps {
  commande: CommandeMS;
  channel: MessageChannel;
  onMarkRead?: (channel: MessageChannel) => void;
  // Bascule d'onglet par `kind` (fournie par le shell) : CTA de fin d'onglet, cf. `cta` ci-dessous.
  goToTab?: (kind: TabKind) => void;
}

// Onglets Q&R / Messagerie de la vue PRESTATAIRE commande-MS (V2). Wrapper mince autour du widget
// partage TabMessagerie, parametre PAR DONNEE (operationId = l'operation MS, channel) — pas de
// branchement role/type, donc partage autorise (charte §2.2), comme cote acheteur (OperationContent).
//   - operationId     : l'operation (la messagerie est par operation, partagee acheteur/prestataires).
//   - operationStatus : statut de l'OPERATION (commande.operation.id_status), PAS celui de la commande
//                       presta. Le widget calibre sa fenetre d'envoi sur les jalons de l'OPERATION
//                       (consultationLancee = idStatus >= 2, prestataireChoisi = idStatus >= 7) et le
//                       backend (MessageOperationController) gate l'envoi sur operation->id_status.
//                       /!\ Ne PAS passer commande.status?.id : la commande gagnante reste a 5 quand
//                       l'operation est deja a 13 -> Q&R doit FERMER (operation>=7) alors que la commande
//                       est <7. Le gating de REACHABILITE des onglets (commandeMsTabs) est independant et
//                       ne couvre pas la fenetre d'envoi (Q&R toujours atteignable). Parite oracle
//                       SuiviDeCommande.tsx:833/861 (commande.operation?.id_status ?? 0).
//   - onMarkRead      : remonte au shell pour vider le badge de non-lus du canal.
//
// AUCUN bandeau n'est rendu ici : TabMessagerie les rend TOUS, pour les deux canaux.
//
// Le bandeau d'anonymat Q&R etait duplique ici, d'ou un doublon a l'ecran pendant la consultation :
// TabMessagerie l'affiche sur `operationStatus === 2`, la copie locale sur commande === ACandidater(3)
// — deux conditions vraies AU MEME MOMENT. C'est la copie locale qui a saute : la fenetre d'envoi se
// calibre sur les jalons de l'OPERATION, pas sur le statut de la commande presta (cf. operationStatus
// ci-dessus). Corollaire : ce bandeau ne suit plus `chronoExpired` cote client, il disparait au
// changement de statut de l'operation — meme comportement que la case acheteur.
//
// Les bandeaux du canal messagerie sont eux aussi rendus PAR TabMessagerie, mais ils ne repondent
// plus au meme pilote :
//   - le bandeau d'ETAT (success "messagerie horodatee active") suit la prop `messagerieActiveSince`.
//     On la passe donc au lieu de le dupliquer ici. Meme condition que la case soeur acheteur
//     (OperationContent, case 'messagerie') : `undefined` tant que le marche n'est pas signe (pas de
//     bandeau) ; une fois signe, la date d'activation = date de signature de l'acheteur, ou `null` si
//     elle est introuvable (bandeau affiche, sans le "depuis le").
//   - la REGLE du canal (info "France Transfert") est desormais PERMANENTE et vit DANS la section,
//     comme la regle d'anonymat en Q&R : c'est une consigne de redaction, elle doit etre lisible
//     avant d'ecrire et non conditionnee a l'ouverture du canal.
const CommandeMsMessagerie: React.FC<CommandeMsMessagerieProps> = ({ commande, channel, onMarkRead, goToTab }) => {
  const chronoExpired = useChronoExpired();

  const marcheSigne = (commande.status?.id ?? commande.id_status) === StatusId.Valider;
  const messagerieActiveSince = marcheSigne
    ? notificationMarche(commande)?.dateHeure ?? null
    : undefined;

  // CTA de bascule, dicte par le CANAL — les deux onglets partagent ce composant mais pas leur suite
  // logique : depuis les Q&R le presta va deposer/relire son offre ; depuis la messagerie (qui n'ouvre
  // qu'au marche signe) il passe a l'evaluation de l'acheteur.
  const cta = channel === 'questions_reponses' ? ctaVersOffre(commande, chronoExpired) : ctaVersEvaluation(commande);

  return (
    <>
      <TabMessagerie
        operationId={commande.operation?.id ?? 0}
        operationStatus={commande.operation?.id_status ?? 0}
        channel={channel}
        onMarkRead={onMarkRead}
        messagerieActiveSince={messagerieActiveSince}
        nextTabButton={
          <NextTabButton label={cta.label} hidden={cta.hidden} onClick={() => goToTab?.(cta.target)} />
        }
      />
    </>
  );
};

export default CommandeMsMessagerie;
